import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TimePicker } from "rsuite";
import { addMinutes, differenceInMinutes } from "date-fns";
import "rsuite/dist/rsuite.min.css";
import { EditTaskTimelog } from "../../../../services/time";

// A single time log entry can't span more than one working day.
const MAX_LOG_MINUTES = 8 * 60;
const clampMinutes = (minutes) => Math.min(Math.max(minutes, 0), MAX_LOG_MINUTES);

function LogTimeModal({
  setIsLogVisible,
  task,
  parentTaskId,
  setLogAdded,
  updated,
  setUpdated,
  setShowEditTimeLogModal,
  showEditTimeLogModal,
  logAdded,
}) {
  const dispatch = useDispatch();

  const [isVisible, setIsVisible] = useState(true);
  const [currentDate, setCurrentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [isBillable, setIsBillable] = useState(true);
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const usersData = useSelector((state) => state.auth.userInfoData);

  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());

  // Spent time is always derived from start/end so the two can never drift
  // out of sync with each other.
  const spentTotalMinutes = clampMinutes(
    differenceInMinutes(endTime, startTime)
  );
  const spentHours = Math.floor(spentTotalMinutes / 60);
  const spentMinutes = spentTotalMinutes % 60;

  const handleStartChange = (newTime) => {
    if (!newTime) return;
    const diff = clampMinutes(differenceInMinutes(endTime, newTime));
    setStartTime(addMinutes(endTime, -diff));
  };

  const handleEndChange = (newTime) => {
    if (!newTime) return;
    const diff = clampMinutes(differenceInMinutes(newTime, startTime));
    setEndTime(addMinutes(startTime, diff));
  };

  const handleSpentHours = (e) => {
    const hours = Math.min(Math.max(Number(e.target.value) || 0, 0), 8);
    setStartTime(addMinutes(endTime, -clampMinutes(hours * 60 + spentMinutes)));
  };

  const handleSpentMinutes = (e) => {
    const minutes = Math.min(Math.max(Number(e.target.value) || 0, 0), 59);
    setStartTime(
      addMinutes(endTime, -clampMinutes(spentHours * 60 + minutes))
    );
  };

  const handleCheckboxClick = () => {
    setIsBillable(!isBillable);
  };

  useEffect(() => {
    if (showEditTimeLogModal && task && task._id) {
      setIsBillable(task?.isBillable);
      setDescription(task?.task_description || "");
      setCurrentDate(new Date(task?.date).toISOString().split("T")[0]);
      setStartTime(new Date(task?.task_start_time));
      setEndTime(new Date(task?.task_end_time));
    }
  }, [task, showEditTimeLogModal]);

  const SubmitLogTime = async () => {
    setIsLoading(true);
    try {
      const timeLogData = {
        user_id: usersData ? usersData._id : null,
        project_id: task ? task?.project_id : null,
        // In edit mode `task` is the timelog record itself, not the parent
        // Task - its own _id is the timelog's id. The caller passes the real
        // parent task id explicitly via parentTaskId for that case. In add
        // mode `task` is the actual Task, so its _id is the task id.
        task_id: parentTaskId || task?._id || null,
        task_description: description,
        task_start_time: startTime,
        task_end_time: endTime,
        task_time_spent: `${spentHours}:${String(spentMinutes).padStart(2, "0")}`,
        isBillable: isBillable,
        isBilled: false,
        date: currentDate,
      };
      if (showEditTimeLogModal) {
        const responseData = await EditTaskTimelog({
          timeLog_id: task._id,
          ...timeLogData,
        });
        if (responseData && responseData.status === 200) {
          toast.success("Time Log Updated successfully");
          setUpdated(!updated);
          setShowEditTimeLogModal(false);
        } else {
          toast.error(responseData.message);
        }
      } else {
        const responseData = await dispatch.time.AddLogTime({
          ...timeLogData,
        });
        if (responseData.timelog) {
          toast.success(responseData.message);
          setLogAdded(!logAdded);
          handleClose();
        } else {
          toast.error(responseData.message);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (showEditTimeLogModal) {
        setShowEditTimeLogModal(false);
      } else if (setIsLogVisible) {
        setIsLogVisible(false);
      }
    }, 500);
  };

  return createPortal(
    <div
      className="pd-theme relative z-[120]"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0 bg-theme-ink opacity-15 z-10"
        onClick={() => handleClose()}
      />
      <div
        className={`fixed lg:min-w-[550px] w-fit z-50 top-[15%] left-1/2 transform -translate-x-1/2 transition-all duration-500 ease-in-out
    ${
      isVisible
        ? "opacity-100 scale-100 translate-y-0"
        : "opacity-0 scale-95 -translate-y-5"
    }
  `}
      >
        <div className="bg-theme-paper px-4 sm:pl-5 sm:pr-6 py-5 shadow-theme-pop rounded-theme-xl">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute right-7 top-7 cursor-pointer text-theme-fg-3"
            onClick={handleClose}
          >
            <path
              d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z"
              fill="currentColor"
            />
          </svg>

          <h1 className="text-2xl font-semibold text-theme-fg-1">Record Time Spent</h1>
          <ul className="mt-3 pl-4 flex flex-col items-start justify-start gap-1">
            <li className="flex items-end justify-start gap-4 mb-4">
              <p className="text-sm text-theme-fg-3 mb-[2px]">Task Name</p>
              <p className="text-lg text-theme-accent font-semibold max-w-full truncate">
                {task ? task?.task_name : null}
              </p>
            </li>
            <li className="flex items-end justify-between gap-3 sm:gap-10 w-full">
              <div className="relative w-full">
                <p className="text-theme-fg-2 mb-1">Date</p>
                <style>
                  {`
                     /* Hide the default calendar icon */
                     input[type="date"]::-webkit-calendar-picker-indicator {
                       display: none;
                    }
                     /* Remove the inner spin button on some browsers */
                     input[type="date"]::-webkit-inner-spin-button {
                       display: none;
                    }
                       input[type="date"]:focus {
                       outline: none; /* Removes default browser focus outline */
                    }
                  `}
                </style>
                <input
                  id="datepicker-title"
                  type="date"
                  className="bg-theme-cream border border-theme-cream-3 text-theme-fg-1 text-sm rounded-theme-sm focus:ring-theme-accent focus:border-theme-accent block w-full pl-4 py-2 appearance-none pr-10 cursor-pointer"
                  value={currentDate}
                  onChange={(e) => setCurrentDate(e.target.value)}
                  onClick={(e) => e.target.showPicker()}
                />
                <div
                  className="absolute top-9 right-3 flex items-center cursor-pointer"
                  onClick={() =>
                    document.getElementById("datepicker-title").showPicker()
                  }
                >
                  <svg
                    className="w-4 h-4 text-theme-fg-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                  </svg>
                </div>
              </div>
              <div className="flex items-center justify-start gap-3 mb-2">
                <input
                  type="checkbox"
                  className="w-6 h-4 rounded-sm cursor-pointer"
                  defaultChecked={isBillable}
                  onClick={handleCheckboxClick}
                  style={{ accentColor: "#534AB7" }}
                />
                <p className="text-theme-fg-2 whitespace-nowrap">
                  Mark as Billing
                </p>
              </div>
            </li>
            <li className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-10">
              <div className="flex items-end gap-4">
                <div>
                  <p className="text-theme-fg-2 mb-1">Start Time</p>
                  <div className="relative min-w-[135px]">
                    <TimePicker
                      format="hh:mm aa"
                      value={startTime}
                      onChange={handleStartChange}
                      size="lg"
                      showMeridian
                      menuStyle={{ zIndex: 9999 }}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-theme-fg-2 mb-1">End Time</p>
                  <div className="relative min-w-[135px]">
                    <div className="relative">
                      <TimePicker
                        format="hh:mm aa"
                        value={endTime}
                        onChange={handleEndChange}
                        size="lg"
                        showMeridian
                        menuStyle={{ zIndex: 9999 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="sm:pt-3">
                <p className="text-theme-fg-2 mb-2">Spent Time</p>
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center justify-end">
                    <input
                      type="number"
                      className="w-16 h-11 rounded-theme-sm border border-theme-cream-3 text-theme-fg-1 cursor-pointer focus:outline-none focus:border-theme-accent text-center"
                      value={spentHours}
                      onChange={(e) => handleSpentHours(e)}
                      min="0"
                      max="8"
                    />
                    <span className="text-[10px] text-theme-fg-3">Hours</span>
                  </div>
                  <div className="flex flex-col items-center justify">
                    <input
                      type="number"
                      className="w-16 h-11 rounded-theme-sm border border-theme-cream-3 text-theme-fg-1 cursor-pointer focus:outline-none focus:border-theme-accent text-center"
                      value={spentMinutes}
                      onChange={(e) => handleSpentMinutes(e)}
                      min="00"
                      max="59"
                    />
                    <span className="text-[10px] text-theme-fg-3">Minutes</span>
                  </div>
                </div>
              </div>
            </li>
            <li className="w-full">
              <textarea
                rows={5}
                className="w-full border rounded-theme-sm text-theme-fg-1 text-sm border-theme-cream-3 focus:outline-none focus:border-theme-accent p-2 mt-1"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter Description..."
              />
            </li>
            <li className="flex items-center justify-end w-full mt-5">
              <div className="flex gap-5">
                <button
                  onClick={handleClose}
                  className="px-6 hover:bg-theme-cream-2 text-sm py-2 rounded-theme-sm text-center text-theme-fg-1 border border-theme-cream-3"
                >
                  Cancel
                </button>
                <button
                  className="bg-theme-ink hover:bg-theme-ink-2 flex items-center justify-center text-white gap-2 px-6 py-2 rounded-theme-sm
              text-sm font-semibold"
                  onClick={SubmitLogTime}
                >
                  <svg
                    width="14"
                    height="11"
                    viewBox="0 0 14 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.6407 0.954736C11.8608 0.753569 12.1537 0.642356 12.4576 0.644563C12.7615 0.646771 13.0526 0.762226 13.2695 0.966567C13.4864 1.17091 13.6121 1.44815 13.6202 1.7398C13.6282 2.03144 13.5179 2.31468 13.3125 2.52974L7.07816 10.0147C6.97096 10.1256 6.84157 10.2145 6.69774 10.2763C6.55391 10.338 6.39858 10.3713 6.24105 10.3741C6.08352 10.3769 5.92702 10.3492 5.7809 10.2926C5.63479 10.236 5.50206 10.1517 5.39066 10.0447L1.25628 6.07574C1.14115 5.97274 1.0488 5.84854 0.98475 5.71054C0.9207 5.57254 0.886259 5.42357 0.883483 5.27252C0.880707 5.12147 0.909652 4.97142 0.968591 4.83134C1.02753 4.69126 1.11526 4.56401 1.22654 4.45718C1.33781 4.35035 1.47037 4.26613 1.61629 4.20955C1.76221 4.15297 1.9185 4.12518 2.07585 4.12785C2.2332 4.13051 2.38837 4.16358 2.53212 4.22506C2.67587 4.28655 2.80525 4.37521 2.91253 4.48574L6.18441 7.62524L11.611 0.987736L11.6407 0.954736Z"
                      fill="white"
                    />
                  </svg>
                  {isLoading
                    ? "Loading.."
                    : showEditTimeLogModal
                    ? "Update Log"
                    : "Log Time"}
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LogTimeModal;
